/**
 * Redux slice for the canonical order state.
 *
 * This is the single source of truth for the active ordering session. All entry
 * points (session create, GET-on-refresh, and direct UI mutations) write through
 * this slice so that auto-save, save-draft, and submit always read the same
 * fully-typed object without recomputation.
 *
 * Reducer semantics:
 *  - setCanonicalOrder     — called after GET /ordering/sessions. Sets isHydrated = true.
 *  - patchCanonicalOrder   — full replacement (legacy); kept for backward compat during migration.
 *  - merge*                — fine-grained partial updates; preferred over patchCanonicalOrder.
 *  - upsertComparator / removeComparator — the single comparator write surface.
 *    removeComparator also nulls the matching legacy mother/father slot so a
 *    removed parent is authoritatively cleared (not silently re-added downstream).
 *  - resetCanonicalOrder   — clear on unmount.
 */

import type {
  CanonicalClinical,
  CanonicalFamily,
  CanonicalGuardianContact,
  CanonicalOrderPayload,
  CanonicalOrderPayloadInbound,
  CanonicalOrderSelection,
  CanonicalPatient,
  CanonicalProviders,
  CanonicalReason,
  Comparator,
  PrimaryConsent,
} from '@/ordering/canonicalOrder'
import { stripPlaceholderDobsFromCanonical } from '@/ordering/canonicalOrder'
import { normalizeInboundParentComparators, parentSideForComparator } from '@/ordering/comparatorWriteAdapter'
import { genComparatorId } from '@/ordering/genComparatorId'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// orderType must be set explicitly via setOrderType; comparator writes do not infer it.

interface CanonicalOrderState {
  canonicalOrder: CanonicalOrderPayload | null
  /** True after a successful GET /ordering/sessions response has been loaded. */
  isHydrated: boolean
}

/**
 * Upsert into the canonical `comparators[]` array, with symmetric sync into
 * the matching legacy `mother` / `father` slot (using the reverse adapter
 * landed in F.1b-ii). Match key is `comparator.id`; when missing or
 * unmatched, the comparator is appended.
 *
 * Flag-independent as of F.1c (GEN-2999) Slice B1.
 */
/**
 * GEN-3010 (Plan E): tiered match for `applyComparatorUpsert`.
 *
 * Order (first match wins):
 *   1. `comparator.id` — strongest, server-issued comparator identity.
 *   2. `comparator.demographic.demographicId` — stable server demographic
 *      identity; the modal preserves it (Plan A) once it has been hydrated
 *      from the legacy slot (Plan C in `enrichComparatorFromLegacySlot`).
 *   3. `relationship` for MOTHER / FATHER only — these slots are 1-of-1, so
 *      matching by relationship is safe and addresses the common case where
 *      the modal saves a comparator that was synthesized from the legacy
 *      parent slot and therefore carries neither `id` nor `demographicId`.
 *
 * Returns -1 to signal "no match — append". Non-parent relationships
 * (SIBLING, TWIN_SIBLING, LEGAL_GUARDIAN, etc.) fall through to append
 * since multiple are valid for those.
 */
function findExistingComparatorIndex(existing: ReadonlyArray<Comparator>, incoming: Comparator): number {
  const id = typeof incoming.id === 'string' && incoming.id.trim() !== '' ? incoming.id : null
  if (id != null) {
    const idx = existing.findIndex((c) => c && c.id === id)
    if (idx >= 0) return idx
  }

  const demoId =
    typeof incoming.demographic?.demographicId === 'string' && incoming.demographic.demographicId.trim() !== ''
      ? incoming.demographic.demographicId
      : null
  if (demoId != null) {
    const idx = existing.findIndex(
      (c) => c && typeof c.demographic?.demographicId === 'string' && c.demographic.demographicId === demoId
    )
    if (idx >= 0) return idx
  }

  const side = parentSideForComparator(incoming)
  if (side != null) {
    return existing.findIndex((c) => c && parentSideForComparator(c) === side)
  }

  return -1
}

/**
 * GEN-3393 follow-up: merge the incoming comparator onto the matched existing
 * row, carrying forward the optional rich sub-blocks when the incoming OMITS
 * them (`key === undefined`).
 *
 * `ComparatorModal.draftToComparator` only serialises `secondaryConsent`,
 * `specimen`, `hpoTerms`, and `icd10Terms` when they actually carry state; an
 * omitted key means "this section was untouched, keep what was there" (see the
 * modal's round-trip-stability contract). Because the upsert previously
 * replaced the matched row wholesale, a focused section save (e.g. editing
 * Demographics) silently erased a sibling section the user had already filled
 * in (e.g. Consent) — the dropped block never reached the PUT, so the value
 * "reverted" to the backend default after a reload.
 *
 * An explicit value (including `null`) on the incoming still wins, so a user
 * can clear a block; only a truly absent key is back-filled from the prior row.
 */
function mergePreservingOptionalBlocks(existing: Comparator, incoming: Comparator): Comparator {
  const merged: Comparator = { ...incoming }
  if (incoming.secondaryConsent === undefined && existing.secondaryConsent !== undefined) {
    merged.secondaryConsent = existing.secondaryConsent
  }
  if (incoming.kitShipmentRequested === undefined && existing.kitShipmentRequested !== undefined) {
    merged.kitShipmentRequested = existing.kitShipmentRequested
  }
  if (incoming.specimen === undefined && existing.specimen !== undefined) {
    merged.specimen = existing.specimen
  }
  if (incoming.hpoTerms === undefined && existing.hpoTerms !== undefined) {
    merged.hpoTerms = existing.hpoTerms
  }
  if (incoming.icd10Terms === undefined && existing.icd10Terms !== undefined) {
    merged.icd10Terms = existing.icd10Terms
  }
  // GEN-3152: `isTestingComparator` can only be dropped implicitly now (the manual
  // toggle is opt-in per caller and hidden on the Add-Comparator / guardian-sync
  // flows, which emit no key → serialised as null). A missing/null incoming must
  // never clobber a prior explicit `true`; an explicit `false` (from an
  // `enableTestingToggle` caller) still wins so a non-testing guardian stays off.
  if (
    (incoming.isTestingComparator === undefined || incoming.isTestingComparator === null) &&
    existing.isTestingComparator === true
  ) {
    merged.isTestingComparator = true
  }
  return merged
}

function applyComparatorUpsert(state: CanonicalOrderState, comparator: Comparator) {
  const co = state.canonicalOrder
  if (!co) return

  const current = Array.isArray(co.comparators) ? co.comparators : []
  const matchIndex = findExistingComparatorIndex(current, comparator)
  const newSide = parentSideForComparator(comparator)

  let nextArr: Comparator[]
  // The row actually stored may be a merged object (not the `comparator`
  // argument), so track its identity for the single-primary-guardian check.
  let upsertedRow: Comparator
  if (matchIndex >= 0) {
    nextArr = current.slice()
    upsertedRow = mergePreservingOptionalBlocks(current[matchIndex], comparator)
    nextArr[matchIndex] = upsertedRow
  } else {
    upsertedRow = comparator
    nextArr = [...current, comparator]
  }

  // Relationship-reassignment integrity (GEN-2999): MOTHER / FATHER are 1-of-1.
  // When this upsert moves a comparator onto a parent side already occupied by
  // a *different* row (e.g. editing the Mother card and switching it to
  // FATHER in a trio), drop the stale occupant so we never keep two MOTHER /
  // two FATHER rows.
  if (newSide != null) {
    const keepIndex = matchIndex >= 0 ? matchIndex : nextArr.length - 1
    nextArr = nextArr.filter((c, i) => i === keepIndex || parentSideForComparator(c) !== newSide)
  }

  // Single-primary-guardian invariant: at most one comparator may be the
  // primary guardian. When this upsert sets it, clear the flag on every other
  // row and reset the optional patient Guardian Contact card (the comparator
  // now owns the guardian info, so the legacy card must not also claim it).
  if (comparator.isPrimaryGuardian === true) {
    nextArr = nextArr.map((c) =>
      c !== upsertedRow && c.isPrimaryGuardian === true ? { ...c, isPrimaryGuardian: false } : c
    )
    if (co.guardianContact != null) {
      co.guardianContact = null
    }
  }

  // GEN-3484: when the proband has opted OUT of incidental findings, every
  // comparator is locked to opt-out. `mergePatient` materializes this on the
  // comparators that exist at opt-out time, but a comparator added/edited
  // AFTERWARD flows through here — so force its `includeIncidentalFindings` to
  // false as well. Without this the row's stored value stays `null` while the
  // UI shows a disabled opt-out, and the submit gate treats `null` as
  // unanswered, deadlocking completion.
  if (co.patient?.incidentalFindingsSelection === 'opt-out') {
    nextArr = nextArr.map((c) =>
      c.secondaryConsent?.includeIncidentalFindings !== false
        ? { ...c, secondaryConsent: { ...c.secondaryConsent, includeIncidentalFindings: false } }
        : c
    )
  }

  co.comparators = nextArr
}

const initialState: CanonicalOrderState = {
  canonicalOrder: null,
  isHydrated: false,
}

const canonicalOrderSlice = createSlice({
  name: 'canonicalOrder',
  initialState,
  reducers: {
    /**
     * Replace the canonical order with data received from GET /ordering/sessions.
     * Sets isHydrated = true so the OrderStart hydration effect runs.
     *
     * GEN-3393: `comparators[]` is the single source of truth. Inbound payloads
     * persisted before the migration may still carry legacy `mother` / `father`
     * slots; `normalizeInboundParentComparators` folds any populated slot into
     * `comparators[]` and strips the slot keys, so Redux state (and the rest of
     * the app) only ever sees the comparators-only shape.
     */
    setCanonicalOrder(state, action: PayloadAction<CanonicalOrderPayloadInbound>) {
      // GEN-3393: fold any legacy mother/father slots into comparators[], then
      // drop the IMDB placeholder DOB (1900-01-01) so reopened orders show an empty
      // mm/dd/yyyy field instead of 01/01/1900 across every hydration entry point.
      state.canonicalOrder = stripPlaceholderDobsFromCanonical(normalizeInboundParentComparators(action.payload))
      state.isHydrated = true
    },

    /**
     * Replace the canonical order with a locally-computed snapshot.
     * Does not change isHydrated.
     * @deprecated Prefer the fine-grained merge* actions instead.
     */
    patchCanonicalOrder(state, action: PayloadAction<CanonicalOrderPayload>) {
      state.canonicalOrder = action.payload
    },

    resetCanonicalOrder(state) {
      state.canonicalOrder = null
      state.isHydrated = false
    },

    // ─── Fine-grained actions ────────────────────────────────────────────────

    /** Shallow-merge into canonical.order (test selection, labName, cliaLabId, encounterCsn). */
    mergeOrderSelection(state, action: PayloadAction<Partial<CanonicalOrderSelection>>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.order = { ...state.canonicalOrder.order, ...action.payload }
    },

    /** Shallow-merge into canonical.patient. */
    mergePatient(state, action: PayloadAction<Partial<CanonicalPatient>>) {
      if (!state.canonicalOrder) return
      const next = { ...state.canonicalOrder.patient, ...action.payload }
      state.canonicalOrder.patient = next
      /** Proband not opted in → comparators cannot opt in for research (CareBus / product rule). */
      if (next.researchContactConsent !== true) {
        const comparators = state.canonicalOrder.comparators
        if (Array.isArray(comparators)) {
          state.canonicalOrder.comparators = comparators.map((c) =>
            c.secondaryConsent?.researchContactConsent === true
              ? { ...c, secondaryConsent: { ...c.secondaryConsent, researchContactConsent: false } }
              : c
          )
        }
      }
      /**
       * GEN-3484: proband opt-out of incidental findings locks every comparator to opt-out.
       * Comparators may only opt in to incidental findings when the proband opts in.
       */
      if (next.incidentalFindingsSelection === 'opt-out') {
        const comparators = state.canonicalOrder.comparators
        if (Array.isArray(comparators)) {
          state.canonicalOrder.comparators = comparators.map((c) =>
            c.secondaryConsent?.includeIncidentalFindings !== false
              ? { ...c, secondaryConsent: { ...c.secondaryConsent, includeIncidentalFindings: false } }
              : c
          )
        }
      }
    },

    /** Shallow-merge into canonical.providers. */
    mergeProviders(state, action: PayloadAction<Partial<CanonicalProviders>>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.providers = { ...state.canonicalOrder.providers, ...action.payload }
    },

    /** Shallow-merge into canonical.clinical. */
    mergeClinical(state, action: PayloadAction<Partial<CanonicalClinical>>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.clinical = { ...state.canonicalOrder.clinical, ...action.payload }
    },

    /** Shallow-merge into canonical.reason. */
    mergeReason(state, action: PayloadAction<Partial<CanonicalReason>>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.reason = { ...state.canonicalOrder.reason, ...action.payload }
    },

    /** Shallow-merge into canonical.family. */
    mergeFamily(state, action: PayloadAction<Partial<CanonicalFamily>>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.family = { ...state.canonicalOrder.family, ...action.payload }
    },

    /** Shallow-merge into canonical.primaryConsent. */
    mergePrimaryConsent(state, action: PayloadAction<Partial<PrimaryConsent>>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.primaryConsent = { ...state.canonicalOrder.primaryConsent, ...action.payload }
    },

    /**
     * Replace optional guardian contact. Pass `null` to clear.
     */
    setGuardianContact(state, action: PayloadAction<CanonicalGuardianContact | null>) {
      if (!state.canonicalOrder) return
      // Use JSON null (like mother/father) so session PATCH includes "guardianContact":null and merge clears.
      if (action.payload === null) {
        state.canonicalOrder.guardianContact = null
        return
      }
      state.canonicalOrder.guardianContact = action.payload
    },

    /** Explicitly set orderType (e.g. PROBAND_ONLY, FAMILY_DUO_MOTHER, FAMILY_TRIO). */
    setOrderType(state, action: PayloadAction<string>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.orderType = action.payload
    },

    /**
     * Set the organization ordering site (GEN-5738) — a plain ``siteName``
     * string from ``ui_config.organizationSiteInformation``. Pass ``null`` to
     * clear. Only relevant for orgs with ``trackOrderSite`` enabled; the value
     * is required at draft-create and allowlist-validated by the provider-api,
     * so the call site gates selection before the create POST.
     */
    setOrderingSite(state, action: PayloadAction<string | null>) {
      if (!state.canonicalOrder) return
      state.canonicalOrder.orderingSite = action.payload
    },

    /**
     * Append-or-replace a `Comparator` in `canonicalOrder.comparators[]`. Match
     * key is `comparator.id`; rows with no id (or a non-matching id) are
     * appended. Enforces the MOTHER / FATHER 1-of-1 and single-primary-guardian
     * invariants (GEN-2999).
     *
     * GEN-3393: `comparators[]` is the single source of truth — no legacy
     * `mother` / `father` slot sync. `<ComparatorModal>` is the sole parent edit
     * surface in OrderInProgress and dispatches this action unconditionally.
     */
    upsertComparator(state, action: PayloadAction<Comparator>) {
      applyComparatorUpsert(state, action.payload)
    },

    /**
     * Remove a `Comparator` from `canonicalOrder.comparators[]` by stable `id`.
     * No-op when the id is not found. Callers own any SKU-tier re-resolution
     * (OrderStart's comparator-count sync effect handles this).
     *
     * Parent slots: when the removed comparator is a MOTHER / FATHER, the legacy
     * `mother` / `father` slot is explicitly set to `null` so the PUT carries
     * `mother:null` / `father:null`. The provider-api merge pops a null slot AND
     * only synthesizes an ABSENT slot from `comparators[]`; without the explicit
     * null, an omitted slot key lets the previously-stored parent survive in the
     * requisition form data — a silent, dishonest re-add of a "removed" parent.
     *
     * Pre-accession-for-rcigm / inflection_test-window only: the call site gates
     * WHEN removal is allowed; the reducer just performs it.
     */
    removeComparator(state, action: PayloadAction<string>) {
      const co = state.canonicalOrder
      if (!co || !Array.isArray(co.comparators)) return
      const id = action.payload
      const removed = co.comparators.find((c) => c?.id === id)
      if (!removed) return
      co.comparators = co.comparators.filter((c) => c?.id !== id)
      // Clear the matching legacy parent slot so the removal is authoritative
      // (see docstring). Uses the canonical `parentSideForComparator` helper so
      // BOTH the canonical (`MOTHER`/`FATHER`) and legacy (`bio_mother`/
      // `bio_father`) relationship literals are recognized — a raw `=== 'MOTHER'`
      // check would miss a hydrated legacy-literal parent and silently re-add it.
      // Use JSON null (like setGuardianContact) so the merge clears rather than
      // preserves.
      const side = parentSideForComparator(removed)
      if (side === 'mother') {
        co.mother = null
      } else if (side === 'father') {
        co.father = null
      }
    },

    /**
     * R5 side-nav: assign a stable client `id` to any comparator that hydrated
     * without one. The side-nav comparator groups + detail view + focused
     * section editors are keyed on `id`, but comparators loaded from the API
     * (esp. parents projected from the legacy `mother` / `father` slots) may
     * carry only `demographic.demographicId` or nothing. Mutates in place — it
     * never re-matches or appends, so it can't create duplicates. No-op when
     * every comparator already has an id. Idempotent and safe to call on every
     * hydration.
     */
    ensureComparatorIds(state) {
      const co = state.canonicalOrder
      if (!co || !Array.isArray(co.comparators)) return
      for (const c of co.comparators) {
        if (c != null && !(typeof c.id === 'string' && c.id.trim() !== '')) {
          c.id = genComparatorId()
        }
      }
    },
  },
})

export const {
  setCanonicalOrder,
  patchCanonicalOrder,
  resetCanonicalOrder,
  mergeOrderSelection,
  mergePatient,
  mergeProviders,
  mergeClinical,
  mergeReason,
  mergeFamily,
  mergePrimaryConsent,
  setGuardianContact,
  setOrderType,
  setOrderingSite,
  upsertComparator,
  removeComparator,
  ensureComparatorIds,
} = canonicalOrderSlice.actions

export default canonicalOrderSlice.reducer
